import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  DataApiResponseLegacy,
  EventStatus,
  MovieStatus,
  ParseInvitesRequest,
  ParseInvitesResult,
  ProductType,
  ResearchGroupEmailParseResultModel,
  ResearchGroupInviteAPIUserMode,
  ResearchGroupInviteLinks,
  ResearchGroupInviteLinksResult,
  ResearchGroupLinkRequest,
  ResearchGroupLinkResponse,
  ResearchInvite,
  ResearchRespondent,
  ResearchType360,
  ReviewType,
  ShopMovieStatus,
  ShopPostStatus,
  ShopType,
  ShowStatus,
  SurveyBillingMode,
  SurveyStatus,
  UserInviteStatus,
  VoucherStatus,
} from '@digilize/shared/definitions/src';
import { handleArrayBufferError } from '@digilize/shared/utils/helpers/src';

@Injectable({
  providedIn: 'root',
})
export class ResearchRespondentsApiService {
  constructor(private http: HttpClient) {}

  readonly BASE_RESEARCH_RESPONDENTS_URL = '/api/v1.0/research-respondents';
  readonly BASE_RESEARCH_RESPONDENTS_URL_V2 = '/api/v2.0/research-respondents';

  getResearchRespondents = (params: any) =>
    this.http.get<DataApiResponseLegacy<ResearchRespondent[]>>(`${this.BASE_RESEARCH_RESPONDENTS_URL}`, { params });

  getResearchRespondent = (id: string) =>
    this.http.get<ResearchRespondent>(`${this.BASE_RESEARCH_RESPONDENTS_URL}/${id}`);

  removeResearchRespondent = (id: string, params: { external: boolean }) =>
    this.http.delete(`${this.BASE_RESEARCH_RESPONDENTS_URL}/${id}`, { params });

  parseInvites = (parseInvitesBody: ParseInvitesRequest) =>
    this.http.post<ParseInvitesResult>(`${this.BASE_RESEARCH_RESPONDENTS_URL}/invites/parse`, parseInvitesBody);

  parseInvitesV2 = (file: File, researchGroupId: string, reportProgress = true) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('research_group_id', researchGroupId);

    return this.http.post<ResearchGroupEmailParseResultModel>(
      `${this.BASE_RESEARCH_RESPONDENTS_URL_V2}/invites/parse`,
      formData
    );
  };

  getParseSample = () =>
    this.http
      .get(`${this.BASE_RESEARCH_RESPONDENTS_URL_V2}/invites/parse/sample`, {
        responseType: 'arraybuffer',
        observe: 'response',
      })
      .pipe(handleArrayBufferError());

  addInvites = (addInvitesBody: ResearchInvite) =>
    this.http.post<ParseInvitesResult>(`${this.BASE_RESEARCH_RESPONDENTS_URL}/invites`, addInvitesBody);

  downloadCsvInvites = (params: any) =>
    this.http.get(`${this.BASE_RESEARCH_RESPONDENTS_URL}/invites/csv`, { params, responseType: 'text' });

  downloadXlsxInvites = (params: DownloadXlsxInvitesQuery) => {
    return this.http
      .get(`${this.BASE_RESEARCH_RESPONDENTS_URL}/invites/xlsx`, {
        params: { ...params },
        responseType: 'arraybuffer',
        observe: 'response',
      })
      .pipe(handleArrayBufferError());
  };

  addAnonymousInvites = (researchGroupInviteLinks: ResearchGroupInviteLinks) =>
    this.http.post<ResearchGroupInviteLinksResult>(
      `${this.BASE_RESEARCH_RESPONDENTS_URL}/invites-anonymous`,
      researchGroupInviteLinks
    );

  downloadAnonymousInvites = (group: string, session: string) =>
    this.http.get(`${this.BASE_RESEARCH_RESPONDENTS_URL}/invites-anonymous/csv`, {
      params: { group, session },
      responseType: 'text',
    });

  addRespondentAndSendInvite = (researchGroupInvite: ResearchGroupInviteAPIUserMode) =>
    this.http.post<ResearchGroupEmailParseResultModel>(
      `${this.BASE_RESEARCH_RESPONDENTS_URL}/invites-api`,
      researchGroupInvite
    );

  getInviteLink = (researchGroupLink: ResearchGroupLinkRequest) =>
    this.http.post<ResearchGroupLinkResponse>(`${this.BASE_RESEARCH_RESPONDENTS_URL}/invites-link`, researchGroupLink);
}

export interface DownloadXlsxInvitesQuery {
  company?: string;
  session?: string;
  session_id?: string;
  shop?: string;
  company_group?: string;
  campaign_period?: string;
  user_status?: string;
  shop_types?: ShopType[];
  group?: string;
  subcategory?: string;
  research_group?: string;
  research?: string;
  product_types?: ProductType[];
  movie_statuses?: MovieStatus[];
  event_statuses?: EventStatus[];
  event_type?: string;
  shop_post_status?: ShopPostStatus;
  shop_movie_statuses?: ShopMovieStatus[];
  movie_show_statuses?: ShowStatus[];
  voucher_status?: VoucherStatus;
  user_invite_status?: UserInviteStatus[];
  notifications_actions?: boolean;
  notification_verbs?: number[];
  broadcast_types?: number[];
  notifications_other?: boolean;
  activity_type?: number;
  survey_billing_mode?: SurveyBillingMode;
  review_types?: ReviewType[];
  review_with_text?: boolean;
  email_statuses?: string[];
  research_survey_statuses?: SurveyStatus[];
  research_360_types?: ResearchType360[];
  active?: boolean;
  campaign?: string;
  auction?: string;
  movie?: string;
  event?: string;
  quiz?: string;
  user?: string;
  activity?: string;
  show?: string;
  look?: string;
  feed?: string;
  course?: string;
  chapter?: string;
  tag?: string;
  day?: string;
  from?: string;
  to?: string;
  archived?: boolean;
  finished?: boolean;
  gender?: string;
  genders?: string[];
  age_from?: number;
  age_to?: number;
  language_from?: string;
  language_to?: string;
  labels?: string[];
  media_types?: string[];
  reminders?: number;
  include?: string;
  exclude?: string;
  search_value?: string;
  search_regex?: string;
  order_column_name?: string;
  order_column_descending?: boolean;
}
